import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";

import NoData from "../containers/NoData";
import Card from "../ui/card/Card";
import styles from "./ImportKeywordStatusTable.module.scss";

export type KeywordStatus = {
  keyword: string;
  status: string;
  error: string[];
};

type ImportKeywordStatusTableProps = {
  data: KeywordStatus[];
  title: string;
  noError?: boolean;
};

const columnHelper = createColumnHelper<KeywordStatus>();

const ImportKeywordStatusTable = ({
  data,
  title,
  noError,
}: ImportKeywordStatusTableProps) => {
  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("keyword", {
        header: "Keyword",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => info.getValue(),
      }),
    ];
    if (!noError) {
      baseColumns.push(
        // @ts-expect-error BE types
        columnHelper.accessor("error", {
          header: "Error",
          cell: (info) =>
            Array.isArray(info.getValue())
              ? info.getValue().join(", ")
              : info.getValue(),
        }),
      );
    }

    return baseColumns;
  }, [noError]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Card title={title}>
      {!data.length ? (
        <NoData title="No Keywords" />
      ) : (
        <div className={styles.statusKeywordTable}>
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default ImportKeywordStatusTable;
